import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import EncryptDialogFields from "../../field/security/EncryptDialogFields";
import {Password} from "primereact/password";
import {Dropdown} from "primereact/dropdown";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Encrypt,
	Functions,
	Information,
	PasswordEncrypt,
	PdfContentEncryption,
	PdfEncryptionKey,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxSecurity,
	ToolboxSecuritySecurity
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Exception from "../../../../shared/exception/Exception";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import {MultiSelect} from "primereact/multiselect";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export enum PrintingType {
	NONE = "none",
	LOW = "low",
	HIGH = "high"
}

export class EncryptDialogDefinition extends PortalDialog<ToolboxSecuritySecurity,
	PortalDialogProperties<ToolboxSecuritySecurity>, PortalDialogState<ToolboxSecuritySecurity>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<EncryptDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.encryptDocument, Functions.getEncryptDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ENCRYPT, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: Encrypt | undefined = this.state.parameter?.data.encrypt;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let printing: PrintingType = PrintingType.NONE;

		if (parameter!.canPrint) {
			printing = PrintingType.LOW
		}

		if (parameter!.canPrintHighRes) {
			printing = PrintingType.HIGH
		}

		let permissions: Map<EncryptDialogFields, boolean> = new Map<EncryptDialogFields, boolean>([
			[
				EncryptDialogFields.CAN_MODIFY,
				FunctionHelper.applyValue(parameter!.canModify, Encrypt.getCanModifyDefault())
			],
			[
				EncryptDialogFields.CAN_ASSEMBLE,
				FunctionHelper.applyValue(parameter!.canAssemble, Encrypt.getCanAssembleDefault())
			],
			[
				EncryptDialogFields.CAN_FILL_IN_FORM,
				FunctionHelper.applyValue(parameter!.canFillInForm, Encrypt.getCanFillInFormDefault())
			],
			[
				EncryptDialogFields.CAN_MODIFY_ANNOTATIONS,
				FunctionHelper.applyValue(parameter!.canModifyAnnotations, Encrypt.getCanModifyAnnotationsDefault())
			],
			[
				EncryptDialogFields.CAN_EXTRACT_CONTENT,
				FunctionHelper.applyValue(parameter!.canExtractContent, Encrypt.getCanExtractContentDefault())
			],
			[
				EncryptDialogFields.CAN_EXTRACT_FOR_ACCESSIBILITY,
				FunctionHelper.applyValue(
					parameter!.canExtractForAccessibility, Encrypt.getCanExtractForAccessibilityDefault()
				)
			]
		]);

		let permissionsValue: Array<EncryptDialogFields> = [];
		for (let [field, value] of permissions.entries()) {
			if (value) {
				permissionsValue.push(field);
			}
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(EncryptDialogFields.OPEN_PASSWORD)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-open-password">
							<Translate {...this.props} data="encrypt.open_password.password" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Password feedback={true} id="control-open-password" toggleMask={true}
									  placeholder={Translate.value("inputs.password.unused")}
									  promptLabel={Translate.value("inputs.password.prompt")}
									  weakLabel={Translate.value("inputs.password.weak")}
									  mediumLabel={Translate.value("inputs.password.medium")}
									  strongLabel={Translate.value("inputs.password.strong")}
									  appendTo={document.getElementById("root")}
									  onChange={(e) => this.onChange(
										  EncryptDialogFields.OPEN_PASSWORD, e.currentTarget.value
									  )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(EncryptDialogFields.PERMISSION_PASSWORD)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-permission-password">
							<Translate {...this.props} data="encrypt.permission_password.password" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Password feedback={true} id="control-permission-password" toggleMask={true}
									  placeholder={Translate.value("inputs.password.unused")}
									  promptLabel={Translate.value("inputs.password.prompt")}
									  weakLabel={Translate.value("inputs.password.weak")}
									  mediumLabel={Translate.value("inputs.password.medium")}
									  strongLabel={Translate.value("inputs.password.strong")}
									  appendTo={document.getElementById("root")}
									  onChange={(e) => this.onChange(
										  EncryptDialogFields.PERMISSION_PASSWORD, e.currentTarget.value
									  )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(EncryptDialogFields.PRINTING)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-printing">
							<Translate {...this.props} data="encrypt.permission_password.printing.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown value={printing} inputId="control-printing"
									  options={[
										  {
											  label: Translate.value(
												  "encrypt.permission_password.printing.none",
												  {ns: "dialog"}
											  ),
											  value: PrintingType.NONE
										  },
										  {
											  label: Translate.value(
												  "encrypt.permission_password.printing.low",
												  {ns: "dialog"}
											  ),
											  value: PrintingType.LOW
										  },
										  {
											  label: Translate.value(
												  "encrypt.permission_password.printing.high",
												  {ns: "dialog"}
											  ),
											  value: PrintingType.HIGH
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  EncryptDialogFields.PRINTING, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(EncryptDialogFields.CONTENT_ENCRYPTION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-content-encryption">
							<Translate {...this.props} data="encrypt.content_encryption.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-content-encryption"
									  value={FunctionHelper.applyValue(
										  parameter?.contentEncryption, Encrypt.getContentEncryptionDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "encrypt.content_encryption.everything", {ns: "dialog"}
											  ),
											  value: PdfContentEncryption.EncryptEverything
										  },
										  {
											  label: Translate.value(
												  "encrypt.content_encryption.metadata", {ns: "dialog"}
											  ),
											  value: PdfContentEncryption.ExposeMetadata
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  EncryptDialogFields.CONTENT_ENCRYPTION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-permissions">
							<Translate {...this.props} data="encrypt.permission_password.permissions"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<MultiSelect display="chip" inputId="control-permissions" className="text-chips"
										 value={permissionsValue}
										 onChange={(e) => {
											 for (let permission of Array.from(permissions.keys())) {
												 this.onChange(
													 permission as EncryptDialogFields,
													 e.value.includes(permission)
												 );
											 }
										 }}
										 options={[
											 {
												 label: Translate.value(
													 "encrypt.permission_password.changeing.modify",
													 {ns: "dialog"}
												 ),
												 value: EncryptDialogFields.CAN_MODIFY
											 },
											 {
												 label: Translate.value(
													 "encrypt.permission_password.changeing.idr",
													 {ns: "dialog"}
												 ),
												 value: EncryptDialogFields.CAN_ASSEMBLE
											 },
											 {
												 label: Translate.value(
													 "encrypt.permission_password.changeing.fs",
													 {ns: "dialog"}
												 ),
												 value: EncryptDialogFields.CAN_FILL_IN_FORM
											 },
											 {
												 label: Translate.value(
													 "encrypt.permission_password.changeing.cfs",
													 {ns: "dialog"}
												 ),
												 value: EncryptDialogFields.CAN_MODIFY_ANNOTATIONS
											 },
											 {
												 label: Translate.value(
													 "encrypt.permission_password.changeing.copy",
													 {ns: "dialog"}
												 ),
												 value: EncryptDialogFields.CAN_EXTRACT_CONTENT
											 },
											 {
												 label: Translate.value(
													 "encrypt.permission_password.changeing.usability",
													 {ns: "dialog"}
												 ),
												 value: EncryptDialogFields.CAN_EXTRACT_FOR_ACCESSIBILITY
											 },
										 ]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(EncryptDialogFields.KEY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-key">
							<Translate {...this.props} data="encrypt.options.key.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-key"
									  value={FunctionHelper.applyValue(
										  parameter?.password?.encryptionKey, PasswordEncrypt.getEncryptionKeyDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "encrypt.options.key.40_rc4", {ns: "dialog"}
											  ),
											  value: PdfEncryptionKey.RC440
										  },
										  {
											  label: Translate.value(
												  "encrypt.options.key.128_rc4", {ns: "dialog"}
											  ),
											  value: PdfEncryptionKey.RC4128
										  },
										  {
											  label: Translate.value(
												  "encrypt.options.key.128_aes", {ns: "dialog"}
											  ),
											  value: PdfEncryptionKey.AES128
										  },
										  {
											  label: Translate.value(
												  "encrypt.options.key.256_aes", {ns: "dialog"}
											  ),
											  value: PdfEncryptionKey.AES256
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  EncryptDialogFields.KEY, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["encrypt-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.encrypt.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.encrypt.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: EncryptDialogFields, value: any): void {
		let parameter: Encrypt = this.state.parameter!.data.encrypt!;

		switch (field) {
			case EncryptDialogFields.OPEN_PASSWORD:
				parameter.password!.open = value;
				break;
			case EncryptDialogFields.PERMISSION_PASSWORD:
				parameter.password!.permission = value;
				break;
			case EncryptDialogFields.PRINTING:
				switch (value) {
					case PrintingType.NONE:
						parameter.canPrint = false;
						parameter.canPrintHighRes = false;
						break;
					case PrintingType.LOW:
						parameter.canPrint = true;
						parameter.canPrintHighRes = false;
						break;
					case PrintingType.HIGH:
						parameter.canPrint = true;
						parameter.canPrintHighRes = true;
						break;
				}
				break;
			case EncryptDialogFields.CAN_MODIFY:
				parameter.canModify = value;
				break;
			case EncryptDialogFields.CAN_ASSEMBLE:
				parameter.canAssemble = value;
				break;
			case EncryptDialogFields.CAN_EXTRACT_CONTENT:
				parameter.canExtractContent = value;
				break;
			case EncryptDialogFields.CAN_EXTRACT_FOR_ACCESSIBILITY:
				parameter.canExtractForAccessibility = value;
				break;
			case EncryptDialogFields.CAN_FILL_IN_FORM:
				parameter.canFillInForm = value;
				break;
			case EncryptDialogFields.CAN_MODIFY_ANNOTATIONS:
				parameter.canModifyAnnotations = value;
				break;
			case EncryptDialogFields.KEY:
				parameter.password!.encryptionKey = value;
				break;
			case EncryptDialogFields.CONTENT_ENCRYPTION:
				parameter.contentEncryption = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-locked"}/>
					<Translate {...this.props} data="encrypt.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: ToolboxSecurity = BaseToolbox.fromJson({
			security: this.state.parameter!.data.toJson()
		});

		if (toolboxOperation.security?.encrypt?.password?.open ===
			toolboxOperation.security?.encrypt?.password?.permission) {
			this.setState({
				exception: new Exception(Translate.value("errors.security.identical", {ns: "message"})),
				submitted: false
			})
			return;
		}

		if (toolboxOperation.security?.encrypt?.password?.open === "" &&
			toolboxOperation.security?.encrypt?.password?.permission === "") {
			this.hide();
			return;
		}

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.encrypt", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ENCRYPT;
	}

	protected getDefaultParameter(): Parameter<ToolboxSecuritySecurity> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const EncryptDialog = React.memo(EncryptDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxSecuritySecurity>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxSecuritySecurity>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default EncryptDialog;